import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  LayoutDashboard,
  Phone,
  ClipboardList,
  BarChart3,
  Trophy,
  Settings,
  Menu,
  MessageSquarePlus,
  X,
  LogOut,
  ChevronDown,
  Play,
  BookOpen,
  Wand2,
  Sun,
  Moon,
  Award,
  Activity,
  Sparkles,
  MessageSquare
} from 'lucide-react';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import KeyboardShortcutsModal from './help/KeyboardShortcutsModal';
import TrialStatusBanner from './billing/TrialStatusBanner';
import UpgradeModal from './billing/UpgradeModal';
import Breadcrumbs from './ui/Breadcrumbs';
import FeedbackWidget, { OPEN_FEEDBACK_EVENT } from './feedback/FeedbackWidget';
import { trackEvent } from '../lib/telemetry';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { profile, role, actualRole, roleOverride, setRoleOverride, isPlatformStaff } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Usage telemetry: one page_view per route change inside the signed-in app.
  useEffect(() => {
    trackEvent('page_view', location.pathname);
  }, [location.pathname]);

  // Usage telemetry: seconds the tab was visible on this page, sent as one
  // page_left per visible stretch, when the tab is hidden or the route changes.
  useEffect(() => {
    const page = location.pathname;
    let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
    const sendVisibleTime = () => {
      if (visibleSince === null) return;
      trackEvent('page_left', { page, seconds: (Date.now() - visibleSince) / 1000 });
      visibleSince = null;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendVisibleTime();
      else if (visibleSince === null) visibleSince = Date.now();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      sendVisibleTime();
    };
  }, [location.pathname]);

  // Reset the broken-logo flag whenever the org's logo URL changes so a newly
  // set (valid) logo gets a fresh chance to load.
  useEffect(() => {
    setLogoError(false);
  }, [organization?.logo_url]);

  // Keyboard shortcuts
  const { showHelpModal, setShowHelpModal } = useKeyboardShortcuts({
    onCloseModal: () => {
      setSidebarOpen(false);
      setUserMenuOpen(false);
    }
  });

  // Apply brand colors as CSS variables
  useEffect(() => {
    if (organization?.colors) {
      const root = document.documentElement;
      const { primary, secondary, accent } = organization.colors;

      if (primary) {
        root.style.setProperty('--brand-primary', primary);
        // Convert hex to RGB for Tailwind compatibility
        const rgb = hexToRgb(primary);
        if (rgb) {
          root.style.setProperty('--brand-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
        }
      }
      if (secondary) {
        root.style.setProperty('--brand-secondary', secondary);
      }
      if (accent) {
        root.style.setProperty('--brand-accent', accent);
      }
    }
  }, [organization?.colors]);

  // Helper to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Org-driven branding: prefer the org's real name over the generic placeholder,
  // fall back to the product name when no org name is set.
  const orgName = organization?.name && organization.name !== 'Your Company'
    ? organization.name
    : null;
  const brandLabel = orgName || 'SellEveryCall';

  // Only show the org logo image when a URL is set AND it actually loaded.
  // A broken/404 logo_url falls back to the brand icon + name instead of a blank.
  const showOrgLogo = Boolean(organization?.logo_url) && !logoError;

  const isTrainingPage = location.pathname === '/training';

  // Hide nav during active call
  if (isTrainingPage) {
    return <main className="min-h-screen">{children}</main>;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login');
  };

  // Navigation items grouped by section with role-based visibility
  const trainingItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
    { to: '/scenarios', icon: Play, label: 'Practice', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
    { to: '/courses', icon: BookOpen, label: 'Courses', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
    { to: '/certificates', icon: Award, label: 'Certificates', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
  ];

  const managementItems = [
    { to: '/my-assignments', icon: ClipboardList, label: 'My Assignments', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
    { to: '/assignments', icon: ClipboardList, label: 'Assign Training', roles: ['manager', 'admin', 'super_admin'] },
    { to: '/reports', icon: BarChart3, label: 'Reports', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
    { to: '/analytics/performance', icon: Activity, label: 'Performance Trends', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
    { to: '/leaderboard', icon: Trophy, label: 'Leaderboard', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
  ];

  const settingsItems = [
    { to: '/setup', icon: Wand2, label: 'Setup Wizard', roles: ['admin', 'super_admin'] },
    { to: '/studio', icon: Sparkles, label: 'Content Studio', roles: ['admin', 'super_admin'] },
    { to: '/feedback', icon: MessageSquare, label: 'Feedback', staffOnly: true },
    { to: '/settings', icon: Settings, label: 'Settings', roles: ['trainee', 'manager', 'admin', 'super_admin'] },
  ];

  const filteredTrainingItems = trainingItems.filter(item => item.roles.includes(role || 'trainee'));
  const filteredManagementItems = managementItems.filter(item => item.roles.includes(role || 'trainee'));
  const filteredSettingsItems = settingsItems.filter(item =>
    item.staffOnly ? isPlatformStaff : item.roles.includes(role || 'trainee')
  );

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/' || location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        data-tutorial="sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-sidebar-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              {showOrgLogo ? (
                <img
                  src={organization.logo_url}
                  alt={brandLabel}
                  onError={() => setLogoError(true)}
                  className="h-8 w-auto max-w-[160px] object-contain"
                />
              ) : (
                <>
                  <div className="w-7 h-7 rounded-md bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                    <Phone className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-base font-semibold text-foreground tracking-tight">{brandLabel}</span>
                </>
              )}
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto min-h-0">
            {/* Training section */}
            <div className="px-4 pt-6 pb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Training</span>
            </div>
            {filteredTrainingItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                data-tutorial={
                  item.to === '/scenarios' ? 'scenarios-link' :
                  item.to === '/my-assignments' ? 'assignments-link' :
                  undefined
                }
                className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                  isActive(item.to)
                    ? 'border-l-2 border-primary-500 bg-accent text-foreground font-medium'
                    : 'border-l-2 border-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            ))}

            {/* Management section */}
            {filteredManagementItems.length > 0 && (
              <>
                <div className="px-4 pt-6 pb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Management</span>
                </div>
                {filteredManagementItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    data-tutorial={
                      item.to === '/my-assignments' ? 'assignments-link' :
                      undefined
                    }
                    className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isActive(item.to)
                        ? 'border-l-2 border-primary-500 bg-accent text-foreground font-medium'
                        : 'border-l-2 border-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Settings section */}
            {filteredSettingsItems.length > 0 && (
              <>
                <div className="px-4 pt-6 pb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Settings</span>
                </div>
                {filteredSettingsItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isActive(item.to)
                        ? 'border-l-2 border-primary-500 bg-accent text-foreground font-medium'
                        : 'border-l-2 border-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </>
            )}
          </nav>

          {/* Role Switcher - Only visible to admins/owners */}
          {(actualRole === 'admin' || actualRole === 'super_admin') && (
            <div className="px-3 py-2 border-t border-border">
              <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <span>View As</span>
              </p>
              <div className="px-3">
                <select
                  value={roleOverride || ''}
                  onChange={(e) => setRoleOverride(e.target.value || null)}
                  className="w-full px-3 py-1.5 bg-background border border-input text-foreground rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Your view ({actualRole || 'trainee'})</option>
                  <option value="trainee">View as: Trainee</option>
                  <option value="manager">View as: Manager</option>
                  <option value="admin">View as: Admin</option>
                </select>
                {roleOverride && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Viewing as {roleOverride}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* User section */}
          <div className="p-3 border-t border-border">
            <div className="flex items-center gap-3 px-3 py-2">
              <img
                src={user?.imageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.full_name || 'User')}&background=3b82f6&color=fff`}
                alt={profile?.full_name || 'User'}
                className="w-8 h-8 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-foreground">{profile?.full_name || 'User'}</p>
                <p className="text-xs capitalize text-muted-foreground">{role || 'trainee'}</p>
              </div>
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button
                onClick={handleSignOut}
                className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Trial/Upgrade Status Banner */}
        <TrialStatusBanner onUpgradeClick={() => setShowUpgradeModal(true)} />

        {/* Top bar (mobile) */}
        <header className="lg:hidden flex items-center justify-between h-16 px-4 bg-background border-b border-border">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-muted-foreground hover:text-foreground"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            {showOrgLogo ? (
              <img
                src={organization.logo_url}
                alt={brandLabel}
                onError={() => setLogoError(true)}
                className="h-7 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <>
                <div className="w-6 h-6 rounded bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                  <Phone className="w-3 h-3 text-white" />
                </div>
                <span className="font-semibold text-foreground tracking-tight">{brandLabel}</span>
              </>
            )}
          </Link>
          <div className="relative flex items-center gap-2">
            <button
              onClick={() => window.dispatchEvent(new Event(OPEN_FEEDBACK_EVENT))}
              className="p-2 text-muted-foreground hover:text-foreground"
              title="Send feedback"
              aria-label="Send feedback"
            >
              <MessageSquarePlus className="w-4 h-4" />
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 text-muted-foreground hover:text-foreground"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2"
            >
              <img
                src={user?.imageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.full_name || 'User')}&background=3b82f6&color=fff`}
                alt={profile?.full_name || 'User'}
                className="w-8 h-8 rounded-full"
              />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-md shadow-lg py-1 z-50">
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-accent"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Breadcrumbs */}
        <Breadcrumbs />

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          >
            {children}
          </motion.div>
        </main>
      </div>

      {/* Keyboard shortcuts help modal */}
      <KeyboardShortcutsModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      {/* Platform feedback widget */}
      <FeedbackWidget />

    </div>
  );
}

export default Layout;
